import React, { useEffect } from 'react';
import { isIOS, osVersion } from 'react-device-detect';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Path } from '../Routing';
import { blueLightCardImages } from '../Theming/images';
import { usePageViewTracking, useQuery } from '../Common/Hooks';
import {
  ANALYTICS_ACTION_LABELS,
  ANALYTICS_PAGE_NAMES,
  trackActivationActionEvent,
  useIsIPad,
} from '../Common/Analytics/utils';

import { getAnalytics, logEvent } from 'firebase/analytics';
import app from '../Firebase';
import Header from './components/Header';
import { BoldText } from './BlueLightCardPluginActivationAlwaysAllow';

const Container = styled.div<{ isIPad?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100dvh;
  max-height: 100dvh;
  max-width: ${(props) => (props.isIPad ? '100%' : '600px')};
  margin: 0 auto;
  overflow: hidden;
`;

const InfoContainer = styled.div<{ isIPad?: boolean }>`
  padding: ${({ isIPad }) => (isIPad ? '160px 20px' : '30px 10px')};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: left;
`;

const InfoTitle = styled.h3<{ isIPad?: boolean }>`
  font-family: 'Museo Sans', sans-serif;
  font-weight: 600;
  font-size: ${({ isIPad }) => (isIPad ? '32px' : '24px')};
  line-height: ${({ isIPad }) => (isIPad ? '40px' : '32px')};
  letter-spacing: 0%;
  vertical-align: middle;
  text-align: center;
  margin: 20px 0 0 24px 0;
`;

const ActivationStepsContainer = styled.div<{ isIPad?: boolean }>`
  font-family: 'Museo Sans', sans-serif;
  flex-direction: row;
  display: flex;
  width: 100%;
  max-width: 500px;
  align-items: flex-start;
  justify-content: center;
  gap: 16px;
  ${({ isIPad }) =>
    isIPad &&
    `
    max-width: 600px;
    margin: 0 auto;
  `}

  & > ol {
    flex: 1;
    margin: 0;
    list-style-type: none;
    color: #202125;
    font-weight: 600;
    font-size: ${({ isIPad }) => (isIPad ? '20px' : '16px')};
    line-height: ${({ isIPad }) => (isIPad ? '28px' : '24px')};
    letter-spacing: 0%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 234px;
    ${({ isIPad }) =>
      isIPad &&
      `
      padding: 0;
      flex: 0 1 auto;
    `}

    & > li {
      padding: 0;
      margin: 0;

      &:first-child {
        padding-top: 0px;
      }

      &:last-child {
        padding-bottom: 25px;
        padding-top: 30px;
      }
    }
  }
`;

const ActivationImage = styled.img<{ isIPad?: boolean }>`
  width: 100px;
  height: 224px;
  object-fit: contain;
  flex-shrink: 0;
  margin-right: 10px;
  ${({ isIPad }) =>
    isIPad &&
    `
    width: 160px;
  `}
`;

const ActivateIcon = styled.img`
  width: 20px;
  height: 20px;
  display: inline-block;
  vertical-align: middle;
  margin: 0 4px;
`;

interface Props {
  sliide?: boolean;
}

interface Message {
  data: string & { type: string; givenPermission?: boolean };
}

const BlueLightCardPluginActivateMultiStep: React.FC<Props> = () => {
  const navigate = useNavigate();
  const iOS18 = isIOS && parseInt(osVersion) >= 18;
  const infoItem = [
    {
      default:
        'Tap the AA icon in the browser bar at the top or bottom of your screen.',
      ios18: (
        <>
          Tap the{' '}
          <ActivateIcon
            src={blueLightCardImages.activate_icon}
            alt="activate icon"
          />{' '}
          icon in the browser bar at the top or bottom of your screen.
        </>
      ),
    },
    {
      default: (
        <>
          Tap <strong>Manage Extensions</strong>.
        </>
      ),
      ios18: (
        <>
          Tap <strong>Manage Extensions</strong>.
        </>
      ),
    },
    {
      default: (
        <>
          Toggle on the <strong>Blue Light Card Deal Finder</strong>
        </>
      ),
      ios18: (
        <>
          Toggle on the <strong>Blue Light Card Deal Finder</strong>
        </>
      ),
    },
  ];

  const infoItemIpad = [
    {
      default: (
        <>
          Tap on the puzzle icon in the browser bar and select{' '}
          <strong>Manage Extensions</strong>
        </>
      ),
      ios18: (
        <>
          Tap on the puzzle icon in the browser bar and select{' '}
          <strong>Manage Extensions</strong>
        </>
      ),
    },
    {
      default: (
        <>
          Toggle on the <strong>Blue Light Card Deal Finder</strong>
        </>
      ),
      ios18: (
        <>
          Toggle on the <strong>Blue Light Card Deal Finder</strong>
        </>
      ),
    },
  ];

  const query = useQuery();
  const { activation_instructions_page } = ANALYTICS_PAGE_NAMES;
  const { activation_permissions } = ANALYTICS_ACTION_LABELS;
  usePageViewTracking(activation_instructions_page, 'BlueLightCard');

  const isIPad = useIsIPad();

  useEffect(() => {
    const analytics = getAnalytics(app);

    const initialHeight = window.innerHeight;
    const interval = setInterval(() => {
      const currentHeight = window.innerHeight;

      if (currentHeight < initialHeight) {
        navigate({
          pathname: Path.BlueLightCardPluginActivationAlwaysAllow,
          search: location.search,
        });
      }
    }, 1500);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const messageHandler = (message: Message) => {
      if (message && message.data === '') {
        window.location.reload();
      } else if (message) {
        switch (message.data?.type) {
          case 'PluginEnabled': {
            const permissionType = message.data?.givenPermission
              ? 'full'
              : 'partial';
            const previousPermissionType = localStorage.getItem(
              'blc_permission_type'
            );
            const hasActivatedBefore =
              localStorage.getItem('blc_plugin_activated') === 'true';

            // Store activation and permission state
            localStorage.setItem('blc_plugin_activated', 'true');
            localStorage.setItem('blc_permission_type', permissionType);
            trackActivationActionEvent(
              query,
              'BlueLightCard',
              activation_permissions,
              activation_instructions_page,
              {
                permissions: message.data?.givenPermission ? 'Full' : 'Partial',
              }
            );
            logEvent(analytics, 'activation_permissions', {
              permissions: message.data?.givenPermission ? 'Full' : 'Partial',
              partner: 'BlueLightCard',
            });

            // Case 1: First time user - default flow
            if (!hasActivatedBefore) {
              if (message.data?.givenPermission) {
                // Full permissions on first activation
                navigate({
                  pathname: Path.BlueLightCardPluginActivationStartShopping,
                  search: location.search,
                });
              } else {
                // Partial permissions on first activation - go to AlwaysAllow screen
                navigate({
                  pathname: Path.BlueLightCardPluginActivationAlwaysAllow,
                  search: location.search,
                });
              }
              return;
            }

            // Case 2: Extension was disabled, had full permissions, now re-enabled with full
            if (
              previousPermissionType === 'full' &&
              message.data?.givenPermission
            ) {
              navigate({
                pathname: Path.BlueLightCardPluginActivationStartShopping,
                search: location.search,
              });
              return;
            }

            // Case 3: Extension was disabled, had partial permissions, now re-enabled with partial
            if (
              previousPermissionType === 'partial' &&
              !message.data?.givenPermission
            ) {
              navigate({
                pathname: Path.BlueLightCardPluginActivationPartialPermissions,
                search: location.search,
              });
              return;
            }

            // Fallback: Route based on current permissions
            if (message.data?.givenPermission) {
              navigate({
                pathname: Path.BlueLightCardPluginActivationStartShopping,
                search: location.search,
              });
            } else {
              navigate({
                pathname: Path.BlueLightCardPluginActivationAlwaysAllow,
                search: location.search,
              });
            }
            break;
          }
        }
      }
    };

    window.addEventListener('message', messageHandler);

    return () => {
      clearInterval(interval);
      window.removeEventListener('message', messageHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return (
    <Container isIPad={isIPad}>
      <Header
        showBrands
        logoWidth={77}
        height={isIPad ? 200 : 160}
        starTopPosition={isIPad ? '100%' : '90%'}
        heading={
          <BoldText>
            It’s easy to activate the Blue Light Card Deal Finder
          </BoldText>
        }
      />
      <InfoContainer isIPad={isIPad}>
        <InfoTitle isIPad={isIPad}>Here’s what you need to do:</InfoTitle>
        <ActivationStepsContainer isIPad={isIPad}>
          <ol>
            {(isIPad ? infoItemIpad : infoItem).map((item, index) => (
              <li key={index}>{iOS18 ? item.ios18 : item.default}</li>
            ))}
          </ol>
          <ActivationImage
            isIPad={isIPad}
            src={
              isIPad
                ? iOS18
                  ? blueLightCardImages.safari_settings_ipad
                  : blueLightCardImages.safari_settings_ios_17_ipad
                : iOS18
                ? blueLightCardImages.safari_settings
                : blueLightCardImages.safari_settings_ios_17
            }
            alt="safari activation settings steps"
          />
        </ActivationStepsContainer>
      </InfoContainer>
    </Container>
  );
};

export default BlueLightCardPluginActivateMultiStep;
